<template>
  <div class="project">
    <nav class="navbar is-mobile is-medium" style="background-color: #30008A;" role="navigation" aria-label="main navigation">
      <div class="navbar-brand">
        <img id="navbar-logo" src="../../assets/projects/dex/DexProject.png" alt="Logo">
      </div>
    </nav>

    <!-- Header Section -->
    <section class="project-header">
      <h1>DeX Platform: Transforming Your Creativity</h1>
      <p>
        Discover the World of DeX – Where Creativity Takes Center Stage!
        Imagine a platform that isn't just about improvement; it's about revolutionizing the way you bring your ideas to life. That's what DeX stands for, and here's a journey through the DeX experience.
      </p>
    </section>

    <!-- DeX Wizard: A Transformational Redesign -->
    <section class="project-section">
      <h2>DeX Wizard: A Transformational Redesign</h2>
      <p>
        In the beginning, DeX embarked on a redesign journey, with each step driven by feedback and a desire to create a more playful and user-friendly design.
        The Wizard, inspired by the old designs, now boasts a fresh style with more character, making it more inviting to the DeX platform.
      </p>

      <!-- Design and Feedback: Your Voice Matters -->
      <h3>Design and Feedback: Your Voice Matters</h3>
      <p>
        The design process was a collaborative journey, where stakeholders and team members provided input. Design validations were done through polls in group Slack channels, and GitHub issues were updated for seamless feedback.
        Your feedback shaped the design, and stakeholders applauded how easy it was to reach our updates.
      </p>
      <!-- Image 1 Placeholder -->
      <!-- Add Bulma Columns and Rows as needed -->
      <div class="columns">
        <div class="column">
          <!-- Insert Image 1 Here -->
        </div>
      </div>
    </section>

    <!-- Wizard Logic: Decoding the Magic -->
    <section class="project-section">
      <h2>Wizard Logic: Decoding the Magic</h2>
      <p>
        Creating the DeX Wizard required a deep dive into the technical intricacies. The use of multiple Angular components and data flow between parent and child components presented its challenges.
      </p>
      <!-- C4 Model: Visualizing DeX Components -->
      <h3>C4 Model: Visualizing DeX Components</h3>
      <p>
        The C4 Model provides an architectural view of DeX, helping us understand its various components and how they interact. Let's explore it through four key diagrams:
      </p>
      <!-- Image Grid: 2x2 -->
      <div class="columns is-multiline">
        <!-- C1: Overview of DeX -->
        <div class="column is-half">
          <h3>C1: Overview of DeX</h3>
          <p>
            C1 provides an encompassing overview of DeX, highlighting the major components and how they interconnect to form the platform's foundation.
          </p>
          <img src="../../assets/projects/dex/c1.jpg" alt="C1: Overview of DeX" />
        </div>

        <!-- C2: A Deeper Look into DeX -->
        <div class="column is-half">
          <h3>C2: A Deeper Look into DeX</h3>
          <p>
            In C2, we delve deeper into DeX's architecture, focusing on the components that make it tick. This diagram reveals the intricacies of the platform's inner workings.
          </p>
          <img src="../../assets/projects/dex/c2.jpg" alt="C2: A Deeper Look into DeX" />
        </div>

        <!-- C3: The Project Add and Wizard Components -->
        <div class="column is-half">
          <h3>C3: The Project Add and Wizard Components</h3>
          <p>
            C3 zooms in on the Project Add and Wizard components, offering insights into their roles and how they collaborate within DeX.
          </p>
          <img src="../../assets/projects/dex/c3.jpg" alt="C3: The Project Add and Wizard Components" />
        </div>

        <!-- C4: UML Data Flow within the Wizard -->
        <div class="column is-half">
          <h3>C4: UML Data Flow within the Wizard</h3>
          <p>
            C4 presents the UML data flow within the Wizard component. It showcases how data is exchanged and processed as you navigate the wizard's steps.
          </p>
          <img src="../../assets/projects/dex/c4.jpg" alt="C4: UML Data Flow within the Wizard" />
        </div>
      </div>
    </section>
    
    <!-- Design Implementation: Building Block by Block -->
    <section class="project-section">
      <h2>Design Implementation: Building Block by Block</h2>
      <p>
        The Project Wizard implementation comprises various components. Let's break it down:
      </p>
      <!-- Manual Component, Wizard Modules, and More -->
      <p>
        Manual Component (Parent): The manual component serves as the parent of multiple wizard pages. It hosts the modal where child components are loaded. It also features the final project form and several modules, including a wizard stepper and functions to receive data from other components.
        Wizard Modules (Children): The wizard consists of several components, each responsible for collecting and transmitting specific data to the parent component. Pages are loaded in a modal within the parent component.
        Wizard Content Component: These components guide you through the wizard steps. They feature headers explaining the step and what's required. Input fields and submission buttons ensure a smooth data input process.
      </p>
      <!-- Image 2 Placeholder -->
      <!-- Add Bulma Columns and Rows as needed -->
<!-- Previous Image Grid -->
<h3>Wizard iterations</h3>
<div class="columns is-multiline is-mobile">
  <div class="column">
    <img src="../../assets/projects/dex/wizard-old1.png" alt="1" />
  </div>
  <div class="column">
    <img src="../../assets/projects/dex/wizard-old2.jpg" alt="2" />
  </div>
  <div class="column">
    <img src="../../assets/projects/dex/wizard-old3.png" alt="3" />
  </div>
</div>

<h3>Final designs</h3>
<!-- Remaining Images Grid -->
<div class="columns is-multiline is-mobile">
  <div class="column">
    <img src="../../assets/projects/dex/wizard-setup-select.jpg" alt="4" />
  </div>
  <div class="column">
    <img src="../../assets/projects/dex/wizard-desktop.png" alt="5" />
  </div>
  <div class="column">
    <img src="../../assets/projects/dex/wizard-mobile.png" alt="5" />
  </div>
</div>

    </section>

    <!-- Automated Testing: Ensuring Reliability -->
    <section class="project-section">
      <h2>Automated Testing: Ensuring Reliability</h2>
      <p>
        In the near future, DeX is planning to implement automatic unit testing to identify and address any potential issues with the form and wizard filling process. The tool of choice is Karma, which will enable the team to ensure that the application functions seamlessly and reliably.
      </p>
      <!-- More Info -->
      <p>
        For further information on unit testing in Angular, refer to this tutorial: <a href="https://blog.logrocket.com/angular-unit-testing-tutorial-examples/">Unit Testing in Angular</a>.
      </p>
      <!-- Image 3 Placeholder -->
      <!-- Add Bulma Columns and Rows as needed -->
      <div class="columns">
        <div class="column">
          <!-- Insert Image 3 Here -->
        </div>
      </div>
    </section>

    <!-- DeX Background & Minor Tasks: The Pursuit of Excellence -->
    <section class="project-section">
      <h2>DeX Background & Minor Tasks: The Pursuit of Excellence</h2>
      <p>
        DeX's journey extends beyond the wizard. It encompasses smaller yet significant tasks like homepage redesign and enhancing project icons. These incremental improvements are part of DeX's commitment to providing users with a cutting-edge platform for their creative endeavors.
      </p>
      <!-- Image 4 Placeholder -->
      <!-- Add Bulma Columns and Rows as needed -->
      <div class="columns">
        <div class="column">
          <h3>Background iterations</h3>
          <img id="navbar-logo" src="../../assets/projects/dex/bg-variations.png" style="width: 100%; height: auto;" alt="Logo">
          <h3>Background final implementation</h3>
          <img id="navbar-logo" src="../../assets/projects/dex/backgroundgif.gif" style="width: 100%; height: auto;" alt="Logo">


        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
  
@import 'project.css';

</style>